import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { blogData } from "../../data/blogData";
import ShimmerButton from "../animations/ShimmerButton";

export const BlogSection = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  
  // Close blog view and restore list in the same page.
  const closeBlogView = () => {
    setSelectedBlog(null);
    requestAnimationFrame(() => {
      document.querySelector("#blog")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 12
      }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3 }
    }
  };

  return (
    <section
      id="blog"
      className="py-20 sm:py-32 bg-warm-bg relative z-20"
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-warm-sage uppercase tracking-[0.2em] text-xs font-bold mb-4"
          >
            Resources
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-warm-brown mb-4"
          >
            Insights & Wellness Reads
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="font-sans text-warm-brown/70 max-w-2xl mx-auto"
          >
            Explore thoughts on mental health, healing, and wellbeing
          </motion.p>
        </div>

        {/* Blog Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
        >
          {blogData.map((blog) => (
            <motion.article
              key={blog.id}
              variants={cardVariants}
              whileHover={{ y: -8 }}
              className="blog-card group bg-warm-card rounded-3xl overflow-hidden border border-warm-rose/10 hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedBlog(blog)}
              data-testid={`blog-card-${blog.id}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="blog-card-img w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-warm-card to-transparent" />
                
                {/* Category Badge */}
                <span className="absolute top-4 left-4 px-3 py-1 bg-warm-sage/90 text-white text-xs font-medium rounded-full">
                  {blog.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-lg text-warm-brown mb-2 group-hover:text-warm-rose transition-colors line-clamp-2">
                  {blog.title}
                </h3>
                <p className="font-sans text-warm-brown/70 text-sm leading-relaxed line-clamp-2 mb-4">
                  {blog.excerpt}
                </p>
                
                {/* Read More */}
                <motion.span
                  className="inline-flex items-center text-warm-rose text-sm font-medium group-hover:underline"
                  whileHover={{ x: 5 }}
                >
                  Read More →
                </motion.span>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>

      {/* Blog Modal */}
      <AnimatePresence>
        {selectedBlog && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeBlogView}
              className="fixed inset-0 bg-warm-brown/50 backdrop-blur-sm z-50"
              data-testid="blog-modal-backdrop"
            />

            {/* Modal */}
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-4 sm:inset-8 md:inset-16 lg:inset-24 bg-warm-card rounded-3xl z-50 overflow-hidden shadow-2xl"
              data-testid="blog-modal"
            >
              {/* Modal Content */}
              <div className="h-full overflow-y-auto">
                {/* Header Image */}
                <div className="relative h-48 sm:h-64">
                  <img
                    src={selectedBlog.image}
                    alt={selectedBlog.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-warm-card via-warm-card/50 to-transparent" />
                  
                  {/* Category Badge */}
                  <span className="absolute bottom-4 left-6 px-4 py-2 bg-warm-sage text-white text-sm font-medium rounded-full">
                    {selectedBlog.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8 lg:p-12 max-w-4xl mx-auto">
                  {/* Back button inside blog container (scrolls with content) */}
                  <button
                    onClick={closeBlogView}
                    className="inline-block mb-4 px-3 py-2 text-sm cursor-pointer bg-transparent border-none text-warm-brown/80 hover:text-warm-rose transition-colors"
                    data-testid="blog-modal-back"
                    aria-label="Back to blog list"
                  >
                    ← Back
                  </button>

                  <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-warm-brown mb-6">
                    {selectedBlog.title}
                  </h2>
                  
                  <div className="prose prose-warm max-w-none">
                    {selectedBlog.content.split('\n\n').map((paragraph, index) => {
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        return (
                          <h3 key={index} className="font-serif text-xl text-warm-brown mt-6 mb-3">
                            {paragraph.replace(/\*\*/g, '')}
                          </h3>
                        );
                      }
                      if (paragraph.includes('**')) {
                        const parts = paragraph.split(/\*\*(.*?)\*\*/g);
                        return (
                          <p key={index} className="font-sans text-warm-brown/80 leading-relaxed mb-4">
                            {parts.map((part, i) => 
                              i % 2 === 1 ? <strong key={i} className="text-warm-brown">{part}</strong> : part
                            )}
                          </p>
                        );
                      }
                      if (paragraph.startsWith('- ')) {
                        const items = paragraph.split('\n').filter(item => item.startsWith('- '));
                        return (
                          <ul key={index} className="list-disc list-inside space-y-2 mb-4 text-warm-brown/80">
                            {items.map((item, i) => (
                              <li key={i}>{item.replace('- ', '')}</li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p key={index} className="font-sans text-warm-brown/80 leading-relaxed mb-4">
                          {paragraph}
                        </p>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  <div className="mt-8 pt-8 border-t border-warm-rose/20 text-center">
                    <p className="text-warm-brown/70 mb-4">
                      Need help with this? Let's talk.
                    </p>
                    <ShimmerButton
                      onClick={() => {
                        closeBlogView();
                        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      data-testid="blog-modal-cta"
                    >
                      Book a Consultation
                    </ShimmerButton>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default BlogSection;
